// ─────────────────────────────────────────────────────────────────────────────
// MATCHING A SIGNED-IN PERSON TO A STAFF RECORD
//
// Lifted out of affinity_auth.js unchanged so the Supabase and Entra sign-in
// modules share it rather than each carrying a copy that drifts. The fallback
// rules in here were worked out against real names and are not worth
// rediscovering.
// ─────────────────────────────────────────────────────────────────────────────

export function identityFromSession(session, staff) {
  if (!session || !session.user) return null;
  const u = session.user;
  const email = (u.email || "").toLowerCase();
  const claims = u.user_metadata || {};
  const displayName = claims.full_name || claims.name || u.email || "Unknown user";

  const list  = staff || [];
  const local = email.split("@")[0];
  const norm  = (v) => String(v || "").toLowerCase().replace(/[^a-z]/g, "");

  const match =
    // 0. the recorded sign-in email. Andy signs in as andy@affinityco.com and
    //    his staff record says andrew.morgan@affinityco.com — both his, and
    //    they never had to agree until Entra started issuing the token. No rule
    //    below can bridge that: the local parts differ and so does the first
    //    name. It has to be recorded, which is what sys_user.sign_in_email is
    //    for.
    //
    //    Tried FIRST, because where somebody has stated the mapping it is the
    //    answer, and the rules below are inference.
    //    Both spellings: sys_user rows carry sign_in_email, the shell's USERS
    //    list carries signInEmail. Reading only one put Andy on Administrator.
    list.find((s) => email &&
      (s.sign_in_email || s.signInEmail || "").toLowerCase() === email) ||
    // 1. exact email
    list.find((s) => (s.email || "").toLowerCase() === email) ||
    // 2. same local part
    list.find((s) => (s.email || "").toLowerCase().split("@")[0] === local) ||
    // 3. firstname. prefix
    list.find((s) => (s.firstName || "") &&
                     local === String(s.firstName).toLowerCase()) ||
    list.find((s) => (s.firstName || "") &&
                     local.startsWith(String(s.firstName).toLowerCase() + ".")) ||
    // 4. the Entra display name against the record name, ignoring punctuation
    list.find((s) => norm(s.name) && norm(s.name) === norm(displayName)) ||
    // 5. surname in the local part with a matching first initial — catches
    //    a.morgan@ and andy.morgan@ against a record for Andrew Morgan
    list.find((s) => {
      const parts = String(s.name || "").toLowerCase().split(/\s+/);
      if (parts.length < 2) return false;
      const surname = parts[parts.length - 1];
      const initial = parts[0][0];
      return surname.length > 2 && local.includes(surname) && local[0] === initial;
    });

  if (!match && typeof console !== "undefined" && console.warn) {
    // Worth surfacing: the person is in, but on the least privileged role,
    // which will look like missing permissions rather than a bad match.
    console.warn("Signed in as " + email + " but no Core staff record matched. "
      + "Role defaulted to Administrator — add the email to the staff record in System admin.");
  }

  return {
    id: match ? match.id : null,
    name: match ? match.name : displayName,
    email,
    role: match ? match.role : "Administrator",   // least privilege on no match
    matched: !!match,
  };
}
